import {isEmpty} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {StartEndData} from '../../../../helpers/calendarHelpers';
import {buildPendingConference, isProviderConferenceType} from '../../../../helpers/models/conferenceHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  ConferenceModel,
  ConferenceStatusesEnum,
  ConferenceTypesEnum,
} from '../../../../models/api/conferenceModel';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {fetchConferenceAsync} from '../../../../services/calendar/conference';
import {getHasInteractedWithConferencingBanner} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {getDefaultVideoConferencing} from '../../../../store/data/preferences/preferencesSelectors';
import {getHasDefaultVideoConferencing} from '../../../../store/featureFlags/featureFlagsSelectors';
import {Text} from '../../../common/text/text';
import {DefaultVideoConferencingBanner} from '../../banners/defaultVideoConferencingBanner';
import {ConferenceDropdownBoxContainer} from './conferenceDropdownBoxContainer';
import {ConferenceToken} from './conferenceToken';

/*
 * Props.
 */

interface ConferenceEditorProps {
  supportedProviderConferenceTypes: ReadonlyArray<ConferenceTypesEnum>;
  conference: ConferenceModel | undefined;
  onConferenceChange: HandlerOf<ConferenceModel | undefined>;
  summary: string;
  startEndData: StartEndData;
  calendarVersionId: number;
  isUnsavedDraft: boolean;
}

/*
 * Styles.
 */

const StyledLoaderWrapperDiv = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5px;
`;

const StyledContentWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 10px;
`;

const StyledConferenceDropdownBoxContainer = styled(ConferenceDropdownBoxContainer)`
  margin: 0px;
  width: fit-content;
`;

/*
 * Component.
 */

export const ConferenceEditor: FC<ConferenceEditorProps> = (props) => {
  const theme = useUserTheme();
  const {startEndData, summary} = props;

  const [status, setStatus] = useState(AsyncStatusesEnum.LOADED);
  const hasDefaultVideoConferencing = useAppSelector(getHasDefaultVideoConferencing);
  const [hasChangedConference, setHasChangedConference] = useState(false);
  const hasInteractedWithConferencingBanner = useAppSelector(getHasInteractedWithConferencingBanner);
  const defaultVideoConferencing = useAppSelector(getDefaultVideoConferencing);

  if (status === AsyncStatusesEnum.LOADING) {
    return (
      <StyledLoaderWrapperDiv>
        <Loader size={VisualSizesEnum.SMALL} />
      </StyledLoaderWrapperDiv>
    );
  }

  const maybeRenderDefaultVideoConferencingBanner = () => {
    const isConferencingBannerHidden =
      hasInteractedWithConferencingBanner ||
      !hasDefaultVideoConferencing ||
      !hasChangedConference ||
      !isEmpty(defaultVideoConferencing);

    if (isConferencingBannerHidden) {
      return null;
    }

    return <DefaultVideoConferencingBanner />;
  };

  // For provider conference types (hangouts, skype meetings, etc.), we show the conference details only
  // if this is not a pending (placeholder) entry.
  if (
    props.conference &&
    (!isProviderConferenceType(props.conference.type) ||
      props.conference.status !== ConferenceStatusesEnum.PENDING)
  ) {
    return (
      <StyledContentWrapperDiv>
        <ConferenceToken
          conference={props.conference}
          onRemoveClick={() => props.onConferenceChange(undefined)}
        />
        {maybeRenderDefaultVideoConferencingBanner()}
      </StyledContentWrapperDiv>
    );
  }

  const onSelectIntegration = async (integration: IntegrationModel) => {
    setStatus(AsyncStatusesEnum.LOADING);

    const conference = await fetchConferenceAsync(
      integration,
      summary,
      startEndData.startDateTime,
      startEndData.endDateTime,
    );

    if (conference) {
      props.onConferenceChange(conference);
      setStatus(AsyncStatusesEnum.LOADED);
      return;
    }

    setStatus(AsyncStatusesEnum.FAILED);
  };

  const onChange = (
    conferenceType: ConferenceTypesEnum | undefined,
    integration: IntegrationModel | undefined,
  ) => {
    setHasChangedConference(true);
    if (integration) {
      // If there is an integration, load the conference using the integration.
      onSelectIntegration(integration);
      return;
    }

    if (conferenceType) {
      // If a type was selected, Build a pending conference.
      props.onConferenceChange(buildPendingConference(conferenceType));
      return;
    }

    // Otherwise, clear the conference.
    props.onConferenceChange(undefined);
  };

  const maybeRenderMeetingLinkAddedText = () => {
    if (!hasDefaultVideoConferencing || !props.conference) {
      return null;
    }
    return (
      <Text color={theme.greys.shade70} fontSize="verySmall">
        <Trans id="conference-editor-meeting-link-added">Meeting link added</Trans>
      </Text>
    );
  };

  return (
    <StyledContentWrapperDiv>
      <div>
        <StyledConferenceDropdownBoxContainer
          conferenceType={props.conference?.type}
          supportedProviderConferenceTypes={props.supportedProviderConferenceTypes}
          onConferenceTypeChange={onChange}
          calendarVersionId={props.calendarVersionId}
          hasChangedConference={hasChangedConference}
          isEditing={!props.isUnsavedDraft}
        />
        {maybeRenderMeetingLinkAddedText()}
      </div>
      {maybeRenderDefaultVideoConferencingBanner()}
    </StyledContentWrapperDiv>
  );
};
